import React, { useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { ListTransFileRecs } from "../../util/API/batch";
import BatchDetailsModal from "./BatchDetailsModal";
import DownloadBatchButton from "./DownloadBatchButton";

const getStatusDisplay = (code) => {
  const map = {
    "-2": "Open",
    "-1": "Review",
    "1": "Approved for Processing",
    "0": "Approved for Processing",
    "5": "Rejected",
    "3": "Completed",
    "2": "Error",
    "4": "Cancelled",
  };
  return map[code] || `Unknown (${code})`;
};

const getStatusColor = (status) => {
  const colors = {
    Open: "#e2e3e5",
    Review: "#fff3cd",
    "Approved for Processing": "#d4edda",
    Rejected: "#f8d7da",
    Completed: "#d1ecf1",
    Error: "#f5c6cb",
    Cancelled: "#d6d8db",
  };
  return colors[status] || "#f8f9fa";
};

const getStatusTextColor = (status) => {
  const colors = {
    Open: "#383d41",
    Review: "#856404",
    "Approved for Processing": "#155724",
    Rejected: "#721c24",
    Completed: "#0c5460",
    Error: "#721c24",
    Cancelled: "#495057",
  };
  return colors[status] || "#333";
};
const cleanFileName = (fileName) => {
  if (!fileName) return "—";

  return fileName
    .replace(/BATCH_-/i, "BATCH-")
    .replace(/&amp;#039;/g, "'")
    .replace(/&#039;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/&/g, "_and_")
    .replace(/[()]/g, "")
    .replace(/[^a-zA-Z0-9\s'_.-]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    // Restore apostrophe: word ending in _s followed by _ or end
    .replace(/([a-zA-Z])_s(?=_|$)/g, "$1's")
    .trim();
};
function PayrollBatchTable({ batches, loading, companyName }) {
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchRecords, setBatchRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const openBatchDetails = async (batch) => {
    setSelectedBatch(batch);
    setRecordsLoading(true);
    setModalOpen(true);

    try {
      const res = await ListTransFileRecs(batch.id);
      if (res.callStatus === "1") {
        const raw = res.payload?.record || [];
        const recordsArray = Array.isArray(raw) ? raw : raw ? [raw] : [];
        const records = recordsArray.map(item => item.Transfilerecs).filter(Boolean);
        setBatchRecords(records);
      } else {
        setBatchRecords([]);
      }
    } catch (err) {
      console.error("Failed to load batch records:", err);
      setBatchRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedBatch(null);
    setBatchRecords([]);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (batches.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8, color: "#666" }}>
        <Typography variant="h6">No payroll batches found </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 3, boxShadow: 3 }}>
        <Table sx={{ minWidth: 650 }} aria-label="payroll batches table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell><strong>Batch ID</strong></TableCell>
              <TableCell><strong>Scheduled Date</strong></TableCell>
              <TableCell><strong>File Name</strong></TableCell>
              {/* <TableCell align="center"><strong>Records</strong></TableCell> */}
              <TableCell align="right"><strong>Total Amount</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
                            <TableCell align="center"><strong>Uploaded By</strong></TableCell>
                            <TableCell align="center"><strong>Approved By</strong></TableCell>
                            <TableCell align="center"><strong>Rejected By</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.map((batch) => (
              <TableRow key={batch.id} hover>
                <TableCell><strong>#{batch.id}</strong></TableCell>
                <TableCell>{batch.scheduledDateStr}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.9rem" }}>{cleanFileName(batch.fileName)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", color: "#1976d2" }}>
  {batch.totalAmountDisplay || "—"}
</TableCell>
                {/* <TableCell align="center"><strong>{batch.records}</strong></TableCell> */}
                <TableCell>
                  <Box
                    sx={{
                      backgroundColor: getStatusColor(batch.status),
                      color: getStatusTextColor(batch.status),
                      padding: "4px 12px",
                      borderRadius: "16px",
                      display: "inline-block",
                      fontSize: "0.85rem",
                      fontWeight: "bold"
                    }}
                  >
                    {batch.status}
                  </Box>
                </TableCell>
                                 <TableCell align="center">{batch.uploadedby}</TableCell>
                                  <TableCell align="center">{batch.approvedby}</TableCell>
                                  <TableCell align="center">{batch.rejectedby}</TableCell>
                <TableCell align="center">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => openBatchDetails(batch)}
                    sx={{ mr: 1 }}
                  >
                    View Details
                  </Button>
                  <DownloadBatchButton batch={batch} companyName={companyName} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <BatchDetailsModal
        open={modalOpen}
        onClose={closeModal}
        batch={selectedBatch}
        records={batchRecords}
        loading={recordsLoading}
        companyName={companyName}
      />
    </>
  );
}
export default PayrollBatchTable;